import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  PlaylistAddCheck as ReviewIcon
} from '@mui/icons-material';
import apiService from '../../services/api';
import ClientReviewsView from '../clinical-supervisor/ClientReviewsView';

const AdminReviewStats = ({ token, userRole }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadStats();
    loadRecentJobs();
    loadUsers();
  }, [selectedUserId, dateRange]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedUserId) params.append('userId', selectedUserId);
      if (dateRange.startDate) params.append('startDate', dateRange.startDate);
      if (dateRange.endDate) params.append('endDate', dateRange.endDate);

      const response = await apiService.get(`/client-reviews/stats?${params.toString()}`);
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error loading review stats:', err);
      setError(err.response?.data?.error || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentJobs = async () => {
    try {
      const params = new URLSearchParams({
        limit: 20,
        offset: 0
      });
      if (selectedUserId) params.append('userId', selectedUserId);

      const response = await apiService.get(`/client-reviews/history?${params.toString()}`);
      setRecentJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Error loading recent jobs:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiService.get('/admin/users');
      const staff = response.data.filter(u =>
        u.role && (
          u.role.includes('therapist') ||
          u.role.includes('clinical_supervisor') ||
          u.role.includes('admin')
        )
      );
      setUsers(staff);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'info';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      case 'queued': return 'warning';
      default: return 'default';
    }
  };

  const getReviewTypeLabel = (type) => {
    const labels = {
      safety_review: 'Safety Review',
      modality_fidelity: 'Therapy Fidelity',
      keyword_search: 'Keyword Search',
      progress_assessment: 'Progress Assessment'
    };
    return labels[type] || type;
  };

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssessmentIcon />
        Client Review System
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Manage Reviews" icon={<ReviewIcon />} iconPosition="start" />
          <Tab label="Statistics" icon={<AssessmentIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content - Manage Reviews */}
      {activeTab === 0 && (
        <ClientReviewsView token={token} userRole={userRole} />
      )}

      {/* Tab Content - Statistics */}
      {activeTab === 1 && (
        <Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                label="User"
              >
                <MenuItem value="">All Users</MenuItem>
                {users.map(user => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={loadStats}>Apply Filters</Button>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedUserId('');
              setDateRange({ startDate: '', endDate: '' });
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* No stats message */}
      {!loading && (!stats || !stats.queue) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No review statistics available yet. Create a client review to see statistics here.
        </Alert>
      )}

      {/* Queue Statistics */}
      {stats && stats.queue && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>Pending</Typography>
                      <Typography variant="h4">{stats.queue.pending || 0}</Typography>
                    </Box>
                    <ScheduleIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>Processing</Typography>
                      <Typography variant="h4">{stats.queue.processing || 0}</Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>Completed</Typography>
                      <Typography variant="h4">{stats.queue.completed || 0}</Typography>
                    </Box>
                    <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom>Failed</Typography>
                      <Typography variant="h4">{stats.queue.failed || 0}</Typography>
                    </Box>
                    <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Cost Statistics */}
          {stats.costs && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography color="textSecondary" gutterBottom>Total Cost</Typography>
                        <Typography variant="h5">
                          {formatCurrency(stats.costs.totalActualCost || 0)}
                        </Typography>
                      </Box>
                      <MoneyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Average Cost per Job</Typography>
                    <Typography variant="h5">
                      {formatCurrency(stats.costs.averageCostPerJob || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Total Jobs</Typography>
                    <Typography variant="h5">{stats.costs.totalJobs || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Recent Jobs Table */}
      <Paper sx={{ mt: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Recent Review Jobs</Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Review Type</TableCell>
                <TableCell>Initiated By</TableCell>
                <TableCell>Clients</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">No review jobs found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recentJobs.map(job => (
                  <TableRow key={job.jobId}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {job.jobId.substring(0, 20)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getReviewTypeLabel(job.reviewType)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {job.initiatedBy?.userName || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {job.targetClients?.length || 0}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.status}
                        size="small"
                        color={getStatusColor(job.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {job.status === 'processing' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2">
                            {job.progress?.processedClients || 0} / {job.progress?.totalClients || 0}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          {job.progress?.processedClients || 0} / {job.progress?.totalClients || 0}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatCurrency(job.costTracking?.actualCostUSD || 0)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Est: {formatCurrency(job.costTracking?.estimatedCostUSD || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(job.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        </Box>
      )}
    </Box>
  );
};

export default AdminReviewStats;
